import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Copy, CheckCircle2, Lock, FileText, Plus, Flag, Shield, ChevronDown, ClipboardCheck, AlertTriangle, Link2, Eye } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { toast } from 'sonner';
import { exportDecisionRecordPDF } from '@/utils/exportUtils';
import { useState } from 'react';
import { ledgerRecords, controlStateSnapshots, oversightEvidenceRecords, integrationConnections } from '@/data/mockData';
import GradeTooltip from '@/components/GradeTooltip';

function ProcessingIntegrityDisclosure({ items, isFailure }) {
  const [showAll, setShowAll] = useState(false);
  const defaultItems = isFailure
    ? items.filter(pi => !pi.ok || pi.label === 'Output Validation' || pi.label === 'Model Version').slice(0, 3)
    : items.slice(0, 2);
  const visible = showAll ? items : defaultItems;
  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {visible.map((pi) => (
          <div key={pi.label} className={`p-2.5 rounded-lg bg-[#080D1A] border ${pi.ok ? 'border-[#1E2A45]' : 'border-[#F59E0B]/30'}`}>
            <div className="text-[9px] text-[#475569] uppercase tracking-wider mb-1.5">{pi.label}</div>
            <div className="flex items-center gap-1.5">
              {pi.ok ? <CheckCircle2 size={12} className="text-[#10B981] flex-shrink-0" /> : <AlertTriangle size={12} className="text-[#F59E0B] flex-shrink-0" />}
              <span className={`text-[11px] font-medium ${pi.ok ? 'text-[#10B981]' : 'text-[#F59E0B]'}`}>{pi.status || pi.detail}</span>
            </div>
            {pi.status && pi.detail && <div className="text-[9px] text-[#64748B] mt-1">{pi.detail}</div>}
            {pi.temporal && <div className="text-[8px] text-[#F59E0B] font-mono mt-1">{pi.temporal}</div>}
          </div>
        ))}
      </div>
      {!showAll && items.length > visible.length && (
        <button onClick={() => setShowAll(true)} className="text-[9px] text-[#6366F1] hover:text-[#818cf8] mt-2">Show all checks →</button>
      )}
      {showAll && <button onClick={() => setShowAll(false)} className="text-[9px] text-[#6366F1] hover:text-[#818cf8] mt-2">Collapse</button>}
    </div>
  );
}

function ChainOfCustodyDisclosure({ steps }) {
  const [showFull, setShowFull] = useState(false);
  if (!steps || steps.length === 0) return null;
  const first = steps[0];
  const last = steps[steps.length - 1];
  return (
    <div>
      {!showFull ? (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="text-center">
              <div className="w-10 h-10 rounded-full bg-[#10B981]/10 border border-[#10B981]/30 flex items-center justify-center"><CheckCircle2 size={16} className="text-[#10B981]" /></div>
              <div className="text-[9px] text-[#F8FAFC] mt-1.5">{first.step}</div>
              <div className="text-[8px] font-mono text-[#475569]">{first.time}</div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-16 h-px bg-[#1E2A45]" />
              <button onClick={() => setShowFull(true)} className="text-[9px] text-[#6366F1] hover:text-[#818cf8] whitespace-nowrap">Show full chain →</button>
              <div className="w-16 h-px bg-[#1E2A45]" />
            </div>
            <div className="text-center">
              <div className="w-10 h-10 rounded-full bg-[#10B981]/15 border-2 border-[#10B981] flex items-center justify-center"><Lock size={16} className="text-[#10B981]" /></div>
              <div className="text-[9px] text-[#F8FAFC] mt-1.5">{last.step}</div>
              <div className="text-[8px] font-mono text-[#475569]">{last.time}</div>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div className="overflow-x-auto pb-2">
            <div className="flex items-start min-w-[800px]">
              {steps.map((step, i) => {
                const isLast = i === steps.length - 1;
                const isWarning = step.step.toLowerCase().includes('warning') || step.step.toLowerCase().includes('flagged');
                return (
                  <div key={i} className="flex items-start flex-1">
                    <div className="flex flex-col items-center text-center">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isWarning ? 'bg-[#F59E0B]/15 border-2 border-[#F59E0B]' : isLast ? 'bg-[#10B981]/15 border-2 border-[#10B981]' : 'bg-[#10B981]/10 border border-[#10B981]/30'}`}>
                        {isWarning ? <AlertTriangle size={16} className="text-[#F59E0B]" /> : isLast ? <Lock size={16} className="text-[#10B981]" /> : <CheckCircle2 size={16} className="text-[#10B981]" />}
                      </div>
                      <div className="mt-2 max-w-[110px]">
                        <div className={`text-[10px] font-medium leading-tight ${isWarning ? 'text-[#F59E0B]' : 'text-[#F8FAFC]'}`}>{step.step}</div>
                        <div className="text-[9px] font-mono text-[#475569] mt-1">{step.time}</div>
                        <div className="text-[9px] text-[#64748B] mt-0.5">{step.component}</div>
                      </div>
                    </div>
                    {!isLast && (
                      <div className="flex-1 flex items-center mt-5 px-1">
                        <div className="w-full h-px bg-[#1E2A45] relative">
                          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-0 h-0 border-l-[5px] border-l-[#1E2A45] border-t-[3px] border-t-transparent border-b-[3px] border-b-transparent" />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <button onClick={() => setShowFull(false)} className="text-[9px] text-[#6366F1] hover:text-[#818cf8] mt-2">Collapse chain</button>
        </div>
      )}
    </div>
  );
}

function CSSGapDetails({ css }) {
  const [showDetail, setShowDetail] = useState(false);
  return (
    <div className="mt-3 pt-3 border-t border-[#F59E0B]/20">
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-mono text-[#F59E0B]">⚠ Control gap at decision time · External Legal Corpus · No exception active</span>
        <button onClick={() => setShowDetail(!showDetail)} className="text-[9px] text-[#6366F1] hover:text-[#818cf8]">{showDetail ? 'Collapse' : 'Details →'}</button>
      </div>
      {showDetail && <div className="text-[10px] text-[#94A3B8] mt-2">{css.gap}</div>}
      <div className="flex items-center gap-3 mt-2">
        <Link to="/dashboard/proof-chain" className="text-[9px] text-[#6366F1] hover:text-[#818cf8]">View Full Control State Record →</Link>
        <button onClick={() => toast.info('Starting remediation workflow')} className="text-[9px] text-[#F59E0B] hover:text-[#FBBF24]">Start Remediation →</button>
      </div>
      <div className="mt-3 pt-3 border-t border-[#1E2A45]">
        <div className="text-[9px] font-semibold text-[#475569] uppercase tracking-wider mb-2">Package Impact at Decision Time</div>
        <div className="space-y-2">
          {[
            { id: 'PKG-2026-Q1-003', name: 'EU AI Act Regulator Package', s: 'warn', detail: '4 controls dependent on CSS v2.1', resolution: 'Regenerated Apr 17 · Valid' },
            { id: 'PKG-2026-Q1-004', name: 'Internal Audit Package', s: 'blocked', detail: 'Could not generate Apr 15–16', resolution: 'Unblocked · Ready' },
            { id: 'SOC 2 AI Sample', name: 'Processing Integrity', s: 'warn', detail: '3 DERs in affected window', resolution: 'Coverage restored' },
          ].map(pkg => (
            <div key={pkg.id} className="flex items-start gap-2 text-[10px]">
              <span className={pkg.s === 'blocked' ? 'text-[#475569]' : 'text-[#F59E0B]'}>{pkg.s === 'blocked' ? '⏸' : '⚠'}</span>
              <div>
                <span className="text-[#F8FAFC] font-mono">{pkg.id}</span> <span className="text-[#64748B]">{pkg.name}</span>
                <div className="text-[#94A3B8]">{pkg.detail}</div>
                <div className="text-[#10B981]">{pkg.resolution}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


const frameworkMappingRows = [
  { framework: 'EU AI Act', control: 'Art. 9 Risk Management', field: 'Risk classification field', primary: true },
  { framework: 'EU AI Act', control: 'Art. 13 Transparency', field: 'Model identity + version hash', primary: true },
  { framework: 'EU AI Act', control: 'Art. 14 Human Oversight', field: 'Human reviewer field', primary: true },
  { framework: 'ISO 42001', control: '6.1 Risk assessment', field: 'Data source verification', primary: true },
  { framework: 'ISO 42001', control: '9.1 Monitoring', field: 'Cryptographic signature + timestamp', primary: true },
  { framework: 'NIST AI RMF', control: 'GOVERN 1.1', field: 'Principal identity field', primary: true },
  { framework: 'NIST AI RMF', control: 'MANAGE 2.2', field: 'Output type + confidence', primary: true },
  { framework: 'SOC 2', control: 'PI1.1 Processing Integrity', field: 'Output validation status', primary: true },
  { framework: 'SOC 2', control: 'CC6.1 Logical Access', field: 'Principal + data access record', primary: true },
  { framework: 'ISO 27001', control: 'A.12.4 Logging and Monitoring', field: 'Immutable timestamp + vault entry', primary: false },
  { framework: 'NIST 800-53', control: 'AU-2 Audit Events', field: 'Evidence Vault entry reference', primary: false },
  { framework: 'NIST 800-53', control: 'CM-3 Change Control', field: 'Model version at decision time', primary: false },
];

const euAiActFull = [
  { id: 'Art. 9', name: 'Risk Management System' }, { id: 'Art. 10', name: 'Data Governance' },
  { id: 'Art. 11', name: 'Technical Documentation' }, { id: 'Art. 12', name: 'Record Keeping' },
  { id: 'Art. 13', name: 'Transparency' }, { id: 'Art. 14', name: 'Human Oversight' },
  { id: 'Art. 15', name: 'Accuracy & Robustness' }, { id: 'Art. 16', name: 'Provider Obligations' },
  { id: 'Art. 17', name: 'Quality Management' },
];

const relatedRecords = [
  { id: 'DER-2026-047831', ts: 'Apr 15, 14:31:04', model: 'GPT-4o 2025-11', workflow: 'Contract Classification', risk: 'HIGH' },
  { id: 'DER-2026-047830', ts: 'Apr 15, 14:30:58', model: 'GPT-4o 2025-11', workflow: 'Contract Classification', risk: 'HIGH' },
  { id: 'DER-2026-047829', ts: 'Apr 15, 14:30:51', model: 'GPT-4o 2025-11', workflow: 'Contract Classification', risk: 'HIGH' },
  { id: 'DER-2026-047828', ts: 'Apr 15, 14:30:44', model: 'GPT-4o 2025-11', workflow: 'Contract Classification', risk: 'HIGH' },
  { id: 'DER-2026-047827', ts: 'Apr 15, 14:30:37', model: 'GPT-4o 2025-11', workflow: 'Contract Classification', risk: 'HIGH' },
];

const auditorControls = [
  { value: 'pi1.1', label: 'SOC 2 · PI1.1 — Output validity and traceability' },
  { value: 'cc6.1', label: 'SOC 2 · CC6.1 Logical Access' },
  { value: 'eu-art-13', label: 'EU AI Act · Art. 13 Transparency' },
  { value: 'iso-6.1', label: 'ISO 42001 · 6.1 Risk Assessment' },
  { value: 'nist-govern-1.1', label: 'NIST AI RMF · GOVERN 1.1' },
];

/* ── Per-record derived data ────────────────────────── */
function getDerMeta(record) {
  const fail = record.isFailure;
  const resolved = record.isResolved;
  return {
    statusText: resolved ? 'EVIDENCE COMPLETE · CONTROL RE-VERIFIED · EXCEPTION CLOSED' : fail ? 'EVIDENCE INCOMPLETE · INTEGRITY WARNING DETECTED' : 'EVIDENCE COMPLETE · NO VIOLATIONS DETECTED',
    statusColor: resolved ? 'bg-[#10B981]/10 border-[#10B981]/20 text-[#10B981]' : fail ? 'bg-[#F59E0B]/10 border-[#F59E0B]/20 text-[#F59E0B]' : 'bg-[#10B981]/10 border-[#10B981]/20 text-[#10B981]',
    riskBadges: fail ? [
      { text: 'Risk Level: HIGH', color: '#EF4444' },
      { text: 'Decision Class: Regulated workflow', color: '#F59E0B' },
      { text: 'Audit Relevance: Potential SOC 2 PI1 control exception · Potential EU AI Act Annex IV deficiency', color: '#EF4444' },
    ] : resolved ? [
      { text: 'Risk Level: MEDIUM', color: '#F59E0B' },
      { text: 'Decision Class: Regulated workflow · resolved', color: '#10B981' },
      { text: 'Audit Relevance: SOC 2 PI1 re-tested PASS · Exception closed', color: '#10B981' },
    ] : [
      { text: 'Risk Level: HIGH', color: '#EF4444' },
      { text: 'Decision Class: Regulated workflow · EU AI Act Annex III', color: '#F59E0B' },
      { text: 'Audit Relevance: SOC 2 sample eligible · EU AI Act audit scope', color: '#6366F1' },
    ],
    significance: fail ? [
      { label: 'Risk Classification', value: 'High-risk regulated workflow · active integrity warning' },
      { label: 'Regulatory Impact', value: 'Human oversight completed · data lineage unverified · Art. 10 gap' },
      { label: 'Audit Impact', value: 'Likely control exception under SOC 2 PI1 · processing integrity evidence incomplete · Potential Annex IV documentation deficiency · Control testing failure risk: HIGH' },
      { label: 'Exception Status', value: '1 integrity warning · unverified upstream source · no approved override' },
    ] : resolved ? [
      { label: 'Risk Classification', value: 'Medium-risk workflow · resolved' },
      { label: 'Regulatory Impact', value: 'Human oversight completed · data lineage re-verified · Art. 10 satisfied' },
      { label: 'Audit Impact', value: 'SOC 2 PI1 re-tested · PASS · ISO 42001 monitoring evidence restored' },
      { label: 'Exception Status', value: 'Exception EXC-2026-0038 closed Apr 8 · control testing confirmed' },
    ] : [
      { label: 'Risk Classification', value: 'High-risk regulated workflow · EU AI Act Annex III' },
      { label: 'Regulatory Impact', value: 'Human oversight required · completed · Art. 14 satisfied' },
      { label: 'Audit Impact', value: 'Eligible for SOC 2 Type II sampling · EU AI Act conformity assessment scope' },
      { label: 'Exception Status', value: 'No linked incidents · no open exceptions · no approved overrides' },
    ],
    sigBorderColor: fail ? '#F59E0B' : resolved ? '#10B981' : '#6366F1',
    variance: fail
      ? { dot: '#F59E0B', icon: true, text: 'Variance detected · External Legal Corpus unverified · Trust score dropped 26 pts · Model trained on flagged source' }
      : { dot: '#10B981', icon: false, text: resolved ? 'No variance · Confidence 0.91 · Within baseline · Control re-verified · Exception closed' : 'No variance detected · Confidence 0.94 within baseline · No new sources · Model unchanged 14 days · No linked exceptions' },
    processingIntegrity: fail ? [
      { label: 'Output validation', ok: true, status: 'Verified', detail: 'Output format matches expected schema' },
      { label: 'Human review status', ok: true, status: 'Reviewed', detail: 'Contract Analyst ID CA-4471 confirmed' },
      { label: 'Source data verification', ok: false, status: 'Unverified', detail: 'External Legal Corpus provenance not established', temporal: 'Unverified for 12 days · Last verified: Apr 3, 2026' },
      { label: 'Model version at decision time', ok: true, status: null, detail: 'GPT-4o 2025-11 · hash verified' },
      { label: 'Post-decision exception flag', ok: false, status: 'Warning', detail: 'Hash mismatch detected', temporal: 'Confidence: 0.71 · Baseline: 0.89 · Deviation: -20% below workflow average' },
    ] : [
      { label: 'Output validation', ok: true, status: 'Verified', detail: 'Output format matches expected schema' },
      { label: 'Human review status', ok: true, status: 'Reviewed', detail: resolved ? 'Legal Analyst ID LA-2287 confirmed' : 'Contract Analyst ID CA-4471 confirmed' },
      { label: 'Source data verification', ok: true, status: 'Verified', detail: resolved ? 'All 3 data sources verified · re-verified Apr 7' : 'All 3 data sources verified at invocation time' },
      { label: 'Model version at decision time', ok: true, status: null, detail: resolved ? 'Claude Sonnet 3.7 · hash verified' : 'GPT-4o 2025-11 · hash verified' },
      { label: 'Post-decision exception flag', ok: true, status: 'None', detail: null },
    ],
    frameworkBadges: fail ? [
      { name: 'EU AI Act', detail: '7/9 controls', ok: false },
      { name: 'ISO 42001', detail: '6/8 controls', ok: false },
      { name: 'NIST AI RMF', detail: '10/10 functions', ok: true },
      { name: 'SOC 2', detail: 'PI1.1 · CC6.1', ok: false },
    ] : [
      { name: 'EU AI Act', detail: '9/9 controls', ok: true },
      { name: 'ISO 42001', detail: '8/8 controls', ok: true },
      { name: 'NIST AI RMF', detail: '10/10 functions', ok: true },
      { name: 'SOC 2', detail: 'PI1.1 · PI1.2 · CC6.1 · CM-1', ok: true },
    ],
    auditorEvidence: fail ? [
      { element: 'Output validation', source: 'Processing Integrity Status block', present: true },
      { element: 'Human reviewer identity', source: 'Decision Context', present: true },
      { element: 'Data source verification', source: 'Data Access Record', present: false },
      { element: 'Model version at decision time', source: 'Processing Integrity Status', present: true },
      { element: 'Cryptographic signature', source: 'Header', present: true },
    ] : [
      { element: 'Output validation', source: 'Processing Integrity Status block', present: true },
      { element: 'Human reviewer identity', source: 'Decision Context', present: true },
      { element: 'Data source verification', source: 'Data Access Record', present: true },
      { element: 'Model version at decision time', source: 'Processing Integrity Status', present: true },
      { element: 'Cryptographic signature', source: 'Header', present: true },
    ],
    resolutionLifecycle: resolved ? [
      { label: 'DETECTED', status: 'complete', time: 'Apr 3, 12:41 UTC', detail: 'Evidence Vault integrity check', duration: '~2 hours' },
      { label: 'INVESTIGATED', status: 'complete', time: 'Apr 4, 09:15 UTC', detail: 'IT Security opened investigation', duration: '~1.5 days' },
      { label: 'REMEDIATION', status: 'complete', time: 'Apr 7, 14:22 UTC', detail: 'External Legal Corpus re-verified', duration: '~2 days' },
      { label: 'CLOSED', status: 'complete', time: 'Apr 8, 10:05 UTC', detail: 'Control re-tested · Confidence restored · SOC 2 PI1: PASS (25 records re-sampled · 0 exceptions · Apr 8, 2026)', ownership: ['Resolved by: Security Engineering · IT Security', 'Approved by: Compliance · J. Mueller · Apr 19, 2026'] },
    ] : null,
    resolutionSummary: resolved ? 'Control re-tested: PASS · Confidence: 0.91 · Baseline: 0.89 · Within acceptable range · Exception closed · Audit package updated' : null,
    resolutionPayoff: resolved ? 'Risk eliminated · Detection to closure: 4 days · Within audit SLA · Controls restored: 9/9 · SOC 2 PI1 re-tested: PASS' : null,
    resolutionContrast: resolved ? 'Previously: integrity warning · 8,203 decisions affected · External Legal Corpus unverified · SOC 2 PI1 exception risk active' : null,
    resolutionBusinessImpact: resolved ? 'No audit findings expected · No regulatory exposure remaining · Record approved for audit package' : null,
    resolutionCloser: resolved ? 'End-to-end evidence package generated automatically · No manual assembly required' : null,
  };
}

export default function DecisionRecordDetail() {
  const { recordId } = useParams();
  const navigate = useNavigate();
  const record = ledgerRecords.find((r) => r.id === recordId) || ledgerRecords[0];
  const meta = getDerMeta(record);
  const [showFrameworkMapping, setShowFrameworkMapping] = useState(false);
  const [showRelated, setShowRelated] = useState(false);
  const [auditorView, setAuditorView] = useState(false);
  const [auditorControl, setAuditorControl] = useState('pi1.1');
  const [auditorFinding, setAuditorFinding] = useState('none');
  const [auditorNotes, setAuditorNotes] = useState('');
  const [remediationComplete, setRemediationComplete] = useState(false);
  return (
    <div data-testid="decision-record-detail" className="max-w-5xl mx-auto space-y-4">
      <button data-testid="back-to-ledger" onClick={() => navigate('/dashboard/decision-ledger')} className="flex items-center gap-2 text-sm text-[#94A3B8] hover:text-[#F8FAFC] transition-colors">
        <ArrowLeft size={16} /> Back to Decision Ledger
      </button>

      {/* Header */}
      <div className={`glass-card p-6 ${record.isFailure ? 'border-t-2 border-t-[#F59E0B]' : 'border-t-2 border-t-[#6366F1]'}`}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <h1 className={`font-mono text-2xl font-bold text-[#F8FAFC] mb-2 ${record.isFailure ? '' : ''}`} style={record.isFailure ? { textShadow: '0 0 20px rgba(245,158,11,0.1)' } : {}}>{record.id}</h1>
            <span className={`text-[11px] font-mono px-3 py-1 rounded-full border ${meta.statusColor}`} data-testid="status-badge">{meta.statusText}</span>
            {/* Proof grade badge */}
            <div className="flex items-center gap-2 mt-2">
              <GradeTooltip grade={record.isFailure ? 'OPERATIONS-GRADE' : 'AUDIT-GRADE'}>
                <span className={`text-[10px] font-mono px-2.5 py-0.5 rounded-full border cursor-help ${record.isFailure ? 'bg-[#F59E0B]/10 border-[#F59E0B]/30 text-[#F59E0B]' : 'bg-[#10B981]/10 border-[#10B981]/30 text-[#10B981]'}`} data-testid="proof-grade-badge">
                  {record.isFailure ? '◆ OPERATIONS-GRADE' : '◆ AUDIT-GRADE'}
                </span>
              </GradeTooltip>
            </div>
          </div>
          <Shield size={18} className="text-[#6366F1]" />
        </div>
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-[#475569]">Signature:</span>
            <code className="font-mono text-[#94A3B8] bg-[#080D1A] px-2.5 py-1 rounded text-xs">{record.signature?.slice(0, 40)}...</code>
            <button data-testid="copy-signature" onClick={() => { navigator.clipboard?.writeText(record.signature || ''); toast.success('Signature copied'); }} className="text-[#6366F1] hover:text-[#818cf8]"><Copy size={13} /></button>
          </div>
          <div className="text-xs text-[#64748B]">{record.fullTimestamp} · {record.location}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap mt-4 pt-4 border-t border-[#1E2A45]" data-testid="risk-classification">
          {meta.riskBadges.map((b) => (
            <span key={b.text} className="text-[10px] font-mono px-2.5 py-1 rounded-full border" style={{ backgroundColor: `${b.color}10`, borderColor: `${b.color}33`, color: b.color }}>{b.text}</span>
          ))}
        </div>
        {/* Evidence enrichment + Proof Chain link */}
        <div className="flex items-center justify-between flex-wrap gap-2 mt-3 pt-3 border-t border-[#1E2A45]">
          <div className="flex items-center gap-1.5 text-[10px]">
            <span className="text-[#475569]">Evidence enriched by:</span>
            {['Sentinel', 'Entra ID', 'Collibra', 'ServiceNow'].map(s => (
              <span key={s} className="font-mono px-1.5 py-0.5 rounded bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/20">{s} ✓</span>
            ))}
            <span className="text-[#64748B] ml-1">4 independent sources</span>
          </div>
          <Link to={`/dashboard/proof-chain`} className="text-[10px] text-[#6366F1] hover:text-[#818cf8] flex items-center gap-1">
            <Link2 size={10} /> View Proof Chain →
          </Link>
        </div>
      </div>

      {/* Decision Significance */}
      <div className={`rounded-xl border border-[#1E2A45] p-4 ${record.isFailure ? 'bg-[#F59E0B]/[0.05]' : 'bg-[#0A0F1E]'}`} style={{ borderLeftWidth: record.isFailure ? 4 : 3, borderLeftColor: meta.sigBorderColor }} data-testid="decision-significance">
        <div className="text-[9px] font-semibold text-[#475569] uppercase tracking-wider mb-2">Decision Significance</div>
        <div className="space-y-1.5">
          {meta.significance.map((s) => (
            <div key={s.label} className="text-[11px]">
              <span className="text-[#F8FAFC] font-semibold">{s.label}:</span>{' '}
              <span className="text-[#94A3B8]">{s.value}</span>
            </div>
          ))}
        </div>
        {record.isFailure && (
          <Link to="/dashboard/incident-review" className="text-[10px] text-[#F59E0B] hover:text-[#FBBF24] mt-2 inline-flex items-center gap-1">
            <AlertTriangle size={10} /> View active investigation
          </Link>
        )}
        {/* Fix 2 — Investigation owner and action loop */}
        {record.isFailure && (
          <div className="mt-3 p-3 rounded-lg bg-[#080D1A] border border-[#F59E0B]/20 space-y-1.5" data-testid="investigation-action-strip">
            <div className="text-[10px]"><span className="text-[#F8FAFC] font-semibold">Investigation Owner:</span> <span className="text-[#94A3B8]">IT Security · J. Mueller assigned</span></div>
            <div className="text-[10px]"><span className="text-[#F8FAFC] font-semibold">Current Status:</span> <span className="text-[#F59E0B]">Under active investigation</span> <span className="text-[#64748B]">· opened Apr 15, 14:31 UTC</span></div>
            <div className="text-[10px]"><span className="text-[#F8FAFC] font-semibold">Required Next Action:</span> <span className="text-[#94A3B8]">Re-verify External Legal Corpus</span> <span className="text-[#64748B]">· due Apr 17, 2026</span></div>
            <div className="text-[10px]"><span className="text-[#F8FAFC] font-semibold">Escalation Level:</span> <span className="text-[#94A3B8]">CISO notified · compliance-team@meridian.global</span></div>
            <div className="flex items-center gap-3 mt-2 pt-2 border-t border-[#1E2A45]">
              <button onClick={() => { navigate('/dashboard/exceptions'); toast.info('Override request initiated for ' + record.id); }} className="text-[10px] font-medium text-[#F59E0B] border border-[#F59E0B]/30 hover:bg-[#F59E0B]/10 px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1">
                Request override <ChevronDown size={10} className="-rotate-90" />
              </button>
              <button onClick={() => toast.info('Approval history: No prior overrides for this record\'s data sources')} className="text-[10px] text-[#6366F1] hover:text-[#818cf8] transition-colors">
                View approval history
              </button>
            </div>
          </div>
        )}
      </div>

      {auditorView ? (
        /* Auditor Test View — Fix 3 */
        <div className="glass-card p-6 space-y-6" data-testid="auditor-test-view">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-[#F8FAFC] uppercase tracking-wider flex items-center gap-2"><ClipboardCheck size={16} className="text-[#6366F1]" /> Auditor Test View</h3>
            <button onClick={() => setAuditorView(false)} className="text-xs text-[#6366F1] hover:text-[#818cf8]">Return to full record view</button>
          </div>

          {/* Control Under Test */}
          <div>
            <div className="text-[10px] font-semibold text-[#475569] uppercase tracking-wider mb-2">Control under test</div>
            <select value={auditorControl} onChange={(e) => setAuditorControl(e.target.value)} data-testid="auditor-control-select" className="w-full px-3 py-2.5 bg-[#080D1A] border border-[#1E2A45] rounded-lg text-sm text-[#F8FAFC] focus:outline-none focus:border-[#6366F1]">
              {auditorControls.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>

          {/* Sample Rationale */}
          <div>
            <div className="text-[10px] font-semibold text-[#475569] uppercase tracking-wider mb-2">Sample rationale</div>
            <div className="p-3 rounded-lg bg-[#080D1A] border border-[#1E2A45] text-xs text-[#94A3B8] leading-relaxed">
              Selected because: <span className="text-[#F8FAFC]">regulated workflow</span> · <span className="text-[#F8FAFC]">HIGH risk classification</span> · in-scope examination period Jan 1 – Apr 15, 2026 · <span className="text-[#F8FAFC]">Contract Classification Pipeline</span>
            </div>
          </div>

          {/* Population Context */}
          <div>
            <div className="text-[10px] font-semibold text-[#475569] uppercase tracking-wider mb-2">Population Context</div>
            <div className="p-3 rounded-lg bg-[#080D1A] border border-[#1E2A45] text-xs text-[#94A3B8] leading-relaxed">
              This record is part of a population of <span className="text-[#F8FAFC] font-mono font-bold">47,832</span> decisions in scope for the current examination period (Jan 1 – Apr 15, 2026) under <span className="text-[#F8FAFC] font-medium">Contract Classification Pipeline</span>.
            </div>
          </div>

          {/* Evidence Attached */}
          <div>
            <div className="text-[10px] font-semibold text-[#475569] uppercase tracking-wider mb-2">Evidence Attached to This Record</div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#1E2A45]">
                  <th className="text-left text-[9px] font-medium text-[#475569] uppercase tracking-wider py-2 px-3">Evidence Element</th>
                  <th className="text-left text-[9px] font-medium text-[#475569] uppercase tracking-wider py-2 px-3">Source in DER</th>
                  <th className="text-left text-[9px] font-medium text-[#475569] uppercase tracking-wider py-2 px-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {meta.auditorEvidence.map((e) => (
                  <tr key={e.element} className="border-b border-[#1E2A45]/50">
                    <td className="py-2.5 px-3 text-xs text-[#F8FAFC]">{e.element}</td>
                    <td className="py-2.5 px-3 text-xs text-[#64748B]">{e.source}</td>
                    <td className="py-2.5 px-3">
                      {e.present
                        ? <span className="text-[10px] text-[#10B981] flex items-center gap-1"><CheckCircle2 size={11} /> Present</span>
                        : <span className="text-[10px] text-[#F59E0B] flex items-center gap-1"><AlertTriangle size={11} /> Missing</span>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Auditor Finding — 3 radio options */}
          <div>
            <div className="text-[10px] font-semibold text-[#475569] uppercase tracking-wider mb-3">Auditor Finding</div>
            <div className="space-y-2 mb-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="finding" value="none" checked={auditorFinding === 'none'} onChange={() => setAuditorFinding('none')} className="accent-[#10B981]" />
                <span className="text-xs text-[#94A3B8]">No exception — control operating effectively</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="finding" value="exception" checked={auditorFinding === 'exception'} onChange={() => setAuditorFinding('exception')} className="accent-[#F59E0B]" />
                <span className="text-xs text-[#94A3B8]">Exception noted — control deviation identified</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="finding" value="na" checked={auditorFinding === 'na'} onChange={() => setAuditorFinding('na')} className="accent-[#475569]" />
                <span className="text-xs text-[#94A3B8]">Not applicable — outside examination scope</span>
              </label>
            </div>
            <textarea data-testid="auditor-notes" value={auditorNotes} onChange={(e) => setAuditorNotes(e.target.value)} placeholder="Document your finding..." className="w-full h-24 px-3 py-2.5 bg-[#080D1A] border border-[#1E2A45] rounded-lg text-xs text-[#F8FAFC] placeholder:text-[#475569] focus:outline-none focus:border-[#6366F1] resize-none" />
            <button data-testid="save-finding" onClick={() => toast.success(auditorFinding === 'none' ? 'Finding saved: No exception' : auditorFinding === 'na' ? 'Finding saved: Not applicable' : 'Exception finding saved', { description: auditorNotes || 'No additional notes' })} className="mt-3 flex items-center gap-2 bg-[#6366F1] hover:bg-[#818cf8] text-white text-xs font-medium px-5 py-2.5 rounded-lg transition-all shadow-[0_0_8px_rgba(99,102,241,0.15)]">
              <CheckCircle2 size={13} /> Save finding
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Decision Context */}
          <div className="glass-card p-6">
            <h3 className="text-xs font-semibold text-[#F8FAFC] uppercase tracking-wider mb-4">Decision Context</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {record.context ? Object.entries(record.context).map(([key, val]) => (
                <div key={key}>
                  <div className="text-[10px] text-[#475569] uppercase tracking-wider mb-1">{key.replace(/([A-Z])/g, ' $1').trim()}</div>
                  <div className="text-sm text-[#94A3B8]">{val}</div>
                </div>
              )) : (
                <>
                  <div><div className="text-[10px] text-[#475569] uppercase mb-1">Model</div><div className="text-[#94A3B8] text-sm">{record.model}</div></div>
                  <div><div className="text-[10px] text-[#475569] uppercase mb-1">Workflow</div><div className="text-[#94A3B8] text-sm">{record.workflow}</div></div>
                  <div><div className="text-[10px] text-[#475569] uppercase mb-1">Principal</div><div className="text-[#94A3B8] font-mono text-xs">{record.principal}</div></div>
                  <div><div className="text-[10px] text-[#475569] uppercase mb-1">Output Type</div><div className="text-[#94A3B8] text-sm">{record.outputType}</div></div>
                </>
              )}
              <div><div className="text-[10px] text-[#475569] uppercase mb-1">Reviewer</div><div className="text-[#94A3B8] text-sm">{record.reviewer || record.context?.reviewer || 'Contract Analyst ID CA-4471 (downstream)'}</div></div>
              <div><div className="text-[10px] text-[#475569] uppercase mb-1">Model Owner</div><div className="text-[#94A3B8] text-sm">K. Patel</div></div>
              <div><div className="text-[10px] text-[#475569] uppercase mb-1">Last Version Approval</div><div className="text-[#94A3B8] text-sm">J. Mueller · Apr 3, 2026</div></div>
              <div><div className="text-[10px] text-[#475569] uppercase mb-1">Human Review Type</div><div className="text-[#94A3B8] text-sm">Mandatory · EU AI Act Art. 14 requirement</div></div>
            </div>

            {/* Control State at Decision Time Panel — replaces old State at Invocation */}
            <div className="mt-5 pt-5 border-t border-[#1E2A45]" data-testid="control-state-panel">
              {(() => {
                const css = controlStateSnapshots.find(c => c.derId === record.id) || controlStateSnapshots[0];
                const hasGap = !!css.gap;
                return (
                  <div className={`rounded-xl border p-5 ${hasGap ? 'border-[#F59E0B]/30 bg-[#F59E0B]/[0.02]' : 'border-[#1E2A45] bg-[#0A0F1E]'}`} style={{ borderLeftWidth: 3, borderLeftColor: hasGap ? '#F59E0B' : '#10B981' }}>
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="text-[10px] font-bold text-[#F8FAFC] uppercase tracking-wider">Control State at Decision Time</h4>
                        <div className="text-[9px] text-[#64748B] mt-0.5">
                          {css.id} · <span style={{ color: css.proofGrade === 'AUDIT-GRADE' ? '#10B981' : '#F59E0B' }}>◆ {css.proofGrade}</span> · {css.reconstructionMethod}
                        </div>
                        <div className="text-[9px] text-[#475569] mt-0.5">Effective at decision time: {css.effectiveAt}</div>
                      </div>
                    </div>
                    {/* Reconstruction Method */}
                    <div className="flex items-center gap-2 mb-3 px-3 py-1.5 rounded-lg bg-[#080D1A] border border-[#1E2A45]">
                      <span className="text-[9px] text-[#475569]">Reconstruction:</span>
                      <span className="text-[10px] text-[#64748B] font-mono">{css.reconstructionMethod}</span>
                    </div>
                    <div className="space-y-1.5">
                      {css.fields.map((f) => (
                        <div key={f.label} className="flex items-center gap-2 text-[11px]">
                          <span className="text-[#94A3B8] w-56 flex-shrink-0">{f.label} <span className="text-[#475569] text-[9px]">(at decision time)</span>:</span>
                          <span className="text-[#F8FAFC] font-medium">{f.value}</span>
                          {f.status === 'ok' && <span className="text-[#10B981] text-[9px]">✅ {f.note}</span>}
                          {f.status === 'warn' && <span className="text-[#F59E0B] text-[9px] flex items-center gap-0.5"><AlertTriangle size={9} /> {f.note}</span>}
                        </div>
                      ))}
                    </div>
                    {css.summary && <div className="mt-3 pt-3 border-t border-[#1E2A45] text-[10px] text-[#10B981] font-mono">◆ Fully compliant control environment at decision time</div>}
                    {css.gap && (
                      <CSSGapDetails css={css} />
                    )}
                    {!css.gap && (
                      <div className="mt-3">
                        <Link to="/dashboard/proof-chain" className="text-[9px] text-[#6366F1] hover:text-[#818cf8]">View Full Control State Record →</Link>
                        <div className="mt-2 text-[9px] text-[#10B981]">No packages were affected at decision time. All active packages remain valid. ◆</div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Processing Integrity Status */}
            <div className="mt-5 pt-5 border-t border-[#1E2A45]" data-testid="processing-integrity">
              <h4 className="text-[10px] font-semibold text-[#475569] uppercase tracking-wider mb-3">
                Processing Integrity Status
                {record.isResolved && <span className="font-normal normal-case tracking-normal text-[#64748B]"> · State as of decision time: Apr 8, 2026 10:05:33 UTC</span>}
              </h4>
              {/* Reconstruction Method row */}
              <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-[#080D1A] border border-[#1E2A45]">
                <span className="text-[10px] text-[#475569]">Reconstruction Method:</span>
                {(() => {
                  const rm = record.isFailure ? 'SDK Capture' : record.isResolved ? 'SDK Capture + ServiceNow GRC' : 'SDK Capture + Collibra';
                  const color = rm.includes('+') ? 'text-[#818CF8]' : rm.includes('Log') || rm.includes('Manual') ? 'text-[#F59E0B]' : 'text-[#64748B]';
                  return <span className={`text-[10px] ${color} font-mono`}>{rm}</span>;
                })()}
              </div>
              <ProcessingIntegrityDisclosure items={meta.processingIntegrity} isFailure={record.isFailure} />
            </div>

            {/* Variance strip — Fix 2 */}
            <div className="mt-4 flex items-center gap-2 px-3 py-2 rounded-lg bg-[#080D1A] border border-[#1E2A45]" data-testid="variance-strip">
              {meta.variance.icon
                ? <AlertTriangle size={12} className="text-[#F59E0B] flex-shrink-0" />
                : <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: meta.variance.dot }} />
              }
              <span className={`text-[10px] font-mono ${meta.variance.icon ? 'text-[#F59E0B]' : 'text-[#64748B]'}`}>{meta.variance.text}</span>
            </div>
          </div>

          {/* Oversight Evidence Record Panel */}
          <div className="glass-card p-6" data-testid="oversight-evidence-record">
            {(() => {
              const oer = oversightEvidenceRecords.find(o => o.derId === record.id) || oversightEvidenceRecords[0];
              const hasIssue = !!oer.escalation;
              return (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-xs font-semibold text-[#F8FAFC] uppercase tracking-wider flex items-center gap-2">
                        <Eye size={14} className="text-[#6366F1]" /> Oversight Evidence Record — {oer.id}
                        <span className="relative group">
                          <span className="text-[#475569] cursor-help text-[10px]" title="Apex records oversight evidence signals — duration, checklist, role, certification, and modification behavior. It does not assert reviewer comprehension.">ⓘ</span>
                        </span>
                      </h3>
                      <div className="text-[9px] text-[#64748B] mt-1">
                        <span style={{ color: oer.proofGrade === 'AUDIT-GRADE' ? '#10B981' : '#F59E0B' }}>◆ {oer.proofGrade}</span> · {oer.requirement}
                      </div>
                      <div className="text-[9px] text-[#475569]">Reconstruction: <span className="text-[#818CF8] font-mono">{oer.reconstructionMethod}</span></div>
                      {oer.reconstructionMethod.includes('+') && <div className="text-[9px] text-[#64748B]">{oer.reconstructionMethod.split('+').length} independent sources · Strongest corroboration tier</div>}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    {[
                      { label: 'Reviewer', value: oer.reviewer },
                      { label: 'Identity Verified (at review)', value: oer.identitySource, ok: oer.identityVerified },
                      { label: 'Reviewer Role (at review time)', value: oer.reviewerRole },
                      { label: 'Review Duration', value: oer.duration },
                      { label: 'Output Complexity Score', value: `${oer.complexityScore} (${oer.complexityDetail})` },
                      { label: 'Duration vs Complexity', value: oer.durationNote, ok: oer.durationVsComplexity === 'ok', warn: oer.durationVsComplexity === 'warn' },
                      { label: 'Checklist Completed', value: oer.checklistCompleted, ok: oer.checklistOk, warn: !oer.checklistOk },
                      { label: 'Changes Made to Output', value: oer.changesMade },
                      { label: 'Rubber-Stamp Risk Score', value: `${oer.rubberStampRisk} — ${oer.rubberStampNote}`, ok: oer.rubberStampRisk === 'LOW', warn: oer.rubberStampRisk === 'HIGH' },
                      { label: 'AI Literacy Status', value: oer.aiLiteracyDetail, ok: oer.aiLiteracy },
                    ].map((row) => (
                      <div key={row.label} className="flex items-start gap-2 text-[11px]">
                        <span className="text-[#94A3B8] w-56 flex-shrink-0">{row.label}:</span>
                        <span className="text-[#F8FAFC]">{row.value}</span>
                        {row.ok && <span className="text-[#10B981] text-[9px]">✅</span>}
                        {row.warn && <span className="text-[#F59E0B] text-[9px] flex items-center gap-0.5"><AlertTriangle size={9} /></span>}
                      </div>
                    ))}
                  </div>
                  {oer.escalation && (
                    <div className="mt-3 pt-3 border-t border-[#F59E0B]/20 text-[10px] text-[#F59E0B] flex items-start gap-1.5">
                      <AlertTriangle size={11} className="flex-shrink-0 mt-0.5" /> {oer.escalation}
                    </div>
                  )}
                  {!oer.escalation && (
                    <div className="mt-3 pt-3 border-t border-[#1E2A45]" />
                  )}
                  <div className="mt-2 flex items-center gap-3">
                    <Link to="/dashboard/proof-chain" className="text-[9px] text-[#6366F1] hover:text-[#818cf8]">View Full Oversight Record →</Link>
                    {oer.escalation && <button onClick={() => toast.info('Requesting secondary review')} className="text-[9px] text-[#F59E0B] hover:text-[#FBBF24]">Request Secondary Review →</button>}
                  </div>
                </>
              );
            })()}
          </div>

          {/* Data Access Record */}
          <div className="glass-card p-6">
            <h3 className="text-xs font-semibold text-[#F8FAFC] uppercase tracking-wider mb-4">Data Access Record</h3>
            {record.dataAccess ? (
              <div className="space-y-2">
                {record.dataAccess.map((d, i) => {
                  const isOk = d.status === 'Verified';
                  return (
                    <div key={i} className={`flex items-center justify-between p-3 rounded-lg bg-[#080D1A] border ${isOk ? 'border-[#1E2A45]' : 'border-[#F59E0B]/30'}`}>
                      <div className="flex items-center gap-3">
                        {isOk ? <CheckCircle2 size={14} className="text-[#10B981]" /> : <AlertTriangle size={14} className="text-[#F59E0B]" />}
                        <div>
                          <span className="text-sm text-[#F8FAFC]">{d.source}</span>
                          <div className={`text-[9px] font-mono mt-0.5 ${isOk ? 'text-[#475569]' : 'text-[#F59E0B]'}`}>
                            {d.source.includes('Contract Corpus') ? 'AWS S3 · eu-central-1' :
                             d.source.includes('Schema') ? 'Azure Blob · West Europe' :
                             d.source.includes('External Legal') ? 'External API · unverified origin' :
                             d.source.includes('Internal Legal') ? 'AWS S3 · eu-central-1' :
                             d.source.includes('Identity') ? 'Azure AD · eu-west-1' :
                             d.scope}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-[10px]">
                        <span className={`font-medium ${isOk ? 'text-[#10B981]' : 'text-[#F59E0B]'}`}>{d.status}</span>
                        <span className="text-[#475569]">{d.scope}</span>
                        <span className="text-[#475569]">{d.access}</span>
                        {isOk ? <CheckCircle2 size={12} className="text-[#10B981]" /> : <AlertTriangle size={12} className="text-[#F59E0B]" />}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : <div className="text-sm text-[#64748B]">No data access records available.</div>}
          </div>

          {/* Regulatory Control Mapping */}
          <div className="glass-card p-6">
            <h3 className="text-xs font-semibold text-[#F8FAFC] uppercase tracking-wider mb-4">Regulatory Control Mapping</h3>
            {/* Fix 5 — Failed controls summary line */}
            {record.isFailure && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-[#F59E0B]/5 border border-[#F59E0B]/20 mb-4" data-testid="failed-controls-summary">
                <AlertTriangle size={13} className="text-[#F59E0B] flex-shrink-0 mt-0.5" />
                <span className="text-[10px] text-[#F59E0B] leading-relaxed">
                  Failed controls: <span className="font-medium">EU AI Act Art. 10 Data Governance</span> · <span className="font-medium">Art. 15 Accuracy & Robustness</span> · <span className="font-medium">SOC 2 PI1.1 Processing Integrity</span> · <span className="font-medium">ISO 42001 monitoring evidence gap</span>
                </span>
              </div>
            )}
            <Accordion type="multiple" defaultValue={record.isFailure ? ['eu-ai-act'] : []} className="space-y-2">
              <AccordionItem value="eu-ai-act" className="border border-[#1E2A45] rounded-lg overflow-hidden bg-[#080D1A]">
                <AccordionTrigger className="px-4 py-3 text-sm text-[#F8FAFC] hover:no-underline [&[data-state=open]]:border-b [&[data-state=open]]:border-[#1E2A45]">
                  <div className="flex items-center gap-3"><span>EU AI Act</span><span className={`text-[10px] font-mono ${(record.regulatoryMapping?.euAiAct?.covered || 9) === (record.regulatoryMapping?.euAiAct?.total || 9) ? 'text-[#10B981]' : 'text-[#F59E0B]'}`}>{record.regulatoryMapping?.euAiAct?.covered || 9}/{record.regulatoryMapping?.euAiAct?.total || 9}</span></div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-3 pt-2">
                  <div className="space-y-1.5">
                    {(record.regulatoryMapping?.euAiAct?.controls || euAiActFull).map((c) => (
                      <div key={c.id} className="flex items-center gap-2 text-xs">
                        {c.status === 'fail' ? <AlertTriangle size={13} className="text-[#EF4444]" /> : <CheckCircle2 size={13} className="text-[#10B981]" />}
                        <span className="text-[#64748B] font-mono w-16">{c.id}</span>
                        <span className={c.status === 'fail' ? 'text-[#EF4444]' : 'text-[#94A3B8]'}>{c.name}</span>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="iso42001" className="border border-[#1E2A45] rounded-lg overflow-hidden bg-[#080D1A]">
                <AccordionTrigger className="px-4 py-3 text-sm text-[#F8FAFC] hover:no-underline">
                  <div className="flex items-center gap-3"><span>ISO/IEC 42001</span><span className={`text-[10px] font-mono ${(record.regulatoryMapping?.iso42001?.covered || 8) === (record.regulatoryMapping?.iso42001?.total || 8) ? 'text-[#10B981]' : 'text-[#F59E0B]'}`}>{record.regulatoryMapping?.iso42001?.covered || 8}/{record.regulatoryMapping?.iso42001?.total || 8}</span></div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-3 pt-2"><div className="text-xs text-[#94A3B8]">{(record.regulatoryMapping?.iso42001?.covered || 8) === (record.regulatoryMapping?.iso42001?.total || 8) ? 'All controls verified. Evidence complete.' : `${record.regulatoryMapping?.iso42001?.covered} of ${record.regulatoryMapping?.iso42001?.total} controls verified. Evidence gaps remain.`}</div></AccordionContent>
              </AccordionItem>
              <AccordionItem value="nist" className="border border-[#1E2A45] rounded-lg overflow-hidden bg-[#080D1A]">
                <AccordionTrigger className="px-4 py-3 text-sm text-[#F8FAFC] hover:no-underline">
                  <div className="flex items-center gap-3"><span>NIST AI RMF</span><span className="text-[10px] font-mono text-[#10B981]">{record.regulatoryMapping?.nistAiRmf?.covered || 10}/{record.regulatoryMapping?.nistAiRmf?.total || 10}</span></div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-3 pt-2"><div className="text-xs text-[#94A3B8]">All functions verified. Evidence complete.</div></AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          {/* Framework Coverage Summary */}
          <div className="glass-card p-6" data-testid="framework-coverage-summary">
            <h3 className="text-xs font-semibold text-[#F8FAFC] uppercase tracking-wider mb-4">Framework Coverage Summary</h3>
            <div className="flex items-center gap-2 flex-wrap mb-3">
              {meta.frameworkBadges.map(fw => (
                <div key={fw.name} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border ${fw.ok ? 'bg-[#6366F1]/10 border-[#6366F1]/20' : 'bg-[#F59E0B]/10 border-[#F59E0B]/20'}`}>
                  {fw.ok ? <CheckCircle2 size={11} className="text-[#10B981]" /> : <AlertTriangle size={11} className="text-[#F59E0B]" />}
                  <span className="text-[10px] text-[#F8FAFC] font-medium">{fw.name}</span>
                  <span className={`text-[10px] font-mono ${fw.ok ? 'text-[#6366F1]' : 'text-[#F59E0B]'}`}>{fw.detail}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-[#64748B]">This decision record provides evidence for 4 frameworks</span>
              <button onClick={() => setShowFrameworkMapping(!showFrameworkMapping)} className="text-[10px] text-[#6366F1] hover:text-[#818cf8] transition-colors flex items-center gap-1">
                {showFrameworkMapping ? 'Hide' : 'View full'} mapping <ChevronDown size={10} className={`transition-transform ${showFrameworkMapping ? 'rotate-180' : ''}`} />
              </button>
            </div>
            {showFrameworkMapping && (
              <div className="mt-4 overflow-x-auto" data-testid="framework-mapping-table">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-[#1E2A45]">
                    <th className="text-left text-[9px] font-medium text-[#475569] uppercase tracking-wider py-2 px-3">Framework</th>
                    <th className="text-left text-[9px] font-medium text-[#475569] uppercase tracking-wider py-2 px-3">Control / Criteria</th>
                    <th className="text-left text-[9px] font-medium text-[#475569] uppercase tracking-wider py-2 px-3">Evidence Field in this DER</th>
                  </tr></thead>
                  <tbody>
                    {frameworkMappingRows.filter(r => r.primary).map((row, i) => (
                      <tr key={i} className="border-b border-[#1E2A45]/50">
                        <td className="py-2 px-3 text-[10px] text-[#94A3B8]">{row.framework}</td>
                        <td className="py-2 px-3 text-[10px] text-[#F8FAFC]">{row.control}</td>
                        <td className="py-2 px-3 text-[10px] text-[#6366F1] font-mono">{row.field}</td>
                      </tr>
                    ))}
                    <tr><td colSpan={3} className="py-2 px-3 text-[9px] text-[#475569] uppercase tracking-wider font-semibold">Additional mapping context</td></tr>
                    {frameworkMappingRows.filter(r => !r.primary).map((row, i) => (
                      <tr key={`ext-${i}`} className="border-b border-[#1E2A45]/30">
                        <td className="py-1.5 px-3 text-[9px] text-[#475569]">{row.framework}</td>
                        <td className="py-1.5 px-3 text-[9px] text-[#64748B]">{row.control}</td>
                        <td className="py-1.5 px-3 text-[9px] text-[#475569] font-mono">{row.field}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Chain of Custody */}
          <div className="glass-card p-6">
            <h3 className="text-xs font-semibold text-[#F8FAFC] uppercase tracking-wider mb-4">Chain of Custody</h3>
            {record.chainOfCustody ? (
              <ChainOfCustodyDisclosure steps={record.chainOfCustody} />
            ) : <div className="text-sm text-[#64748B]">Chain of custody data not available.</div>}
          </div>

          {/* Related Records — Simplified */}
          <div className={`glass-card p-5 ${record.isFailure ? 'border-l-[3px] border-l-[#F59E0B]' : ''}`} data-testid="related-records">
            <h3 className={`text-xs font-bold uppercase tracking-wider mb-3 ${record.isFailure ? 'text-[#F59E0B]' : 'text-[#F8FAFC]'}`}>
              {record.isFailure ? 'Blast Radius & Related Records' : 'Related Records'}
            </h3>
            <div className="space-y-1.5">
              {record.isFailure ? (
                <>
                  <div className="flex items-center justify-between text-[10px]">
                    <span className="text-[#F59E0B]">Related decisions: <Link to="/dashboard/decision-ledger" className="underline decoration-dotted underline-offset-2">143 affected</Link> · same upstream source · last 30 days</span>
                    <Link to="/dashboard/decision-ledger" className="text-[#6366F1] hover:text-[#818cf8]">Browse →</Link>
                  </div>
                  <div className="flex items-center justify-between text-[10px]">
                    <span className="text-[#F59E0B]">Linked incidents: <Link to="/dashboard/incident-review" className="underline decoration-dotted underline-offset-2">1 active</Link> · <Link to="/dashboard/exceptions" className="underline decoration-dotted underline-offset-2">1 exception</Link></span>
                    <Link to="/dashboard/incident-review" className="text-[#6366F1] hover:text-[#818cf8]">View →</Link>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between text-[10px]">
                    <span className="text-[#64748B]">Related decisions: 847 similar · same workflow · last 30 days</span>
                    <Link to="/dashboard/decision-ledger" className="text-[#6366F1] hover:text-[#818cf8]">Browse →</Link>
                  </div>
                  <div className="flex items-center justify-between text-[10px]">
                    <span className="text-[#64748B]">Linked incidents: 0 active · 0 exceptions</span>
                    <Link to="/dashboard/incident-review" className="text-[#6366F1] hover:text-[#818cf8]">View →</Link>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Resolution Lifecycle — failure and resolved records */}
          {(record.isFailure || record.isResolved) && (
            <div className="glass-card p-6" data-testid="resolution-lifecycle">
              <h3 className="text-xs font-semibold text-[#F8FAFC] uppercase tracking-wider mb-6 flex items-center gap-3">
                Resolution Lifecycle
                {record.isResolved && <span className="text-[10px] font-normal normal-case tracking-normal text-[#10B981]/80">Audit package ready · Exported Apr 19, 2026</span>}
              </h3>
              {meta.resolutionPayoff && (
                <div className="mb-8" data-testid="resolution-payoff">
                  <div className="flex items-center gap-2.5 px-4 py-3 rounded-lg" style={{ borderLeftWidth: 3, borderLeftColor: '#10B981', backgroundColor: 'rgba(16,185,129,0.08)' }}>
                    <Shield size={16} className="text-[#10B981] flex-shrink-0" />
                    <span className="text-sm font-semibold text-[#10B981]">{meta.resolutionPayoff}</span>
                  </div>
                  {meta.resolutionContrast && (
                    <div className="flex items-center gap-1.5 text-[10px] text-[#94A3B8]/70 mt-2 ml-1 italic">
                      <AlertTriangle size={10} className="text-[#F59E0B] flex-shrink-0" />
                      {meta.resolutionContrast}
                    </div>
                  )}
                </div>
              )}
              <div className="flex items-start min-w-0">
                {(meta.resolutionLifecycle || [
                  { label: 'DETECTED', status: 'complete', time: 'Apr 15, 11:14 UTC', detail: 'Evidence Vault integrity check' },
                  { label: 'INVESTIGATING', status: remediationComplete ? 'complete' : 'active', time: 'Apr 15, 14:31 UTC', detail: 'IT Security opened investigation · Owner: J. Mueller' },
                  { label: 'REMEDIATION', status: remediationComplete ? 'complete' : 'pending', time: remediationComplete ? 'Completed' : 'Due Apr 17, 2026', detail: remediationComplete ? 'External Legal Corpus re-verified' : 'Re-verify External Legal Corpus · Assigned: IT Security' },
                  { label: 'CLOSED', status: remediationComplete ? 'active' : 'pending', time: remediationComplete ? 'Pending re-test' : '', detail: 'Re-test required after remediation · SOC 2 PI1 re-examination needed' },
                ]).map((stage, i, arr) => {
                  const isComplete = stage.status === 'complete';
                  const isActive = stage.status === 'active';
                  const isPending = stage.status === 'pending';
                  const isLast = i === arr.length - 1;
                  return (
                    <div key={stage.label} className="flex items-start flex-1">
                      <div className="flex flex-col items-center text-center">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                          isComplete ? 'bg-[#10B981]/15 border-2 border-[#10B981]' :
                          isActive ? 'bg-[#F59E0B]/15 border-2 border-[#F59E0B]' :
                          'bg-[#1E2A45]/30 border border-[#1E2A45]'
                        }`}>
                          {isComplete && <CheckCircle2 size={16} className="text-[#10B981]" />}
                          {isActive && <div className="w-3 h-3 rounded-full bg-[#F59E0B] animate-pulse" />}
                          {isPending && <div className="w-3 h-3 rounded-full bg-[#1E2A45]" />}
                        </div>
                        <div className="mt-2 max-w-[140px]">
                          <div className={`text-[10px] font-bold uppercase tracking-wider ${
                            isComplete ? 'text-[#10B981]' : isActive ? 'text-[#F59E0B]' : 'text-[#475569]'
                          }`}>{stage.label}</div>
                          {stage.duration && <div className="text-[8px] text-[#475569]">{stage.duration}</div>}
                          {stage.time && <div className="text-[9px] font-mono text-[#475569] mt-1">{stage.time}</div>}
                          <div className={`text-[9px] mt-0.5 leading-relaxed ${isComplete || isActive ? 'text-[#94A3B8]' : 'text-[#475569]'}`}>{stage.detail}</div>
                          {stage.ownership && stage.ownership.map((line, oi) => (
                            <div key={oi} className="text-[8px] text-[#475569] mt-0.5">{line}</div>
                          ))}
                        </div>
                      </div>
                      {!isLast && (
                        <div className="flex-1 flex items-center mt-5 px-1">
                          <div className={`w-full h-0.5 ${isComplete ? 'bg-[#10B981]' : 'bg-[#1E2A45]'}`} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="text-[10px] text-[#475569] font-mono mt-4 pt-3 border-t border-[#1E2A45]">
                {meta.resolutionSummary || (remediationComplete ? 'Remediation complete — awaiting re-test and SOC 2 PI1 re-examination' : 'Estimated closure: Apr 19, 2026 · SLA: 4 days remaining · Control testing will resume after source re-verification')}
              </div>
              {meta.resolutionBusinessImpact && (
                <div className="text-[10px] text-[#10B981] font-medium mt-3 pb-3">{meta.resolutionBusinessImpact}</div>
              )}
              {meta.resolutionCloser && (
                <div className="text-[10px] text-[#10B981]/70 mt-2 pb-2">{meta.resolutionCloser}</div>
              )}
              {record.isResolved && (
                <Link to="/dashboard/change-control" className="text-[10px] text-[#6366F1]/70 hover:text-[#818cf8] mt-2 inline-flex items-center gap-1 transition-colors">
                  View remediation record <ChevronDown size={10} className="-rotate-90" /> Change Control EX-2026-0038
                </Link>
              )}
              {record.isFailure && !remediationComplete && (
                <button
                  data-testid="mark-remediation-complete"
                  onClick={() => { setRemediationComplete(true); toast.success('Remediation marked complete — re-test stage activated'); }}
                  className="mt-3 flex items-center gap-2 text-xs font-medium text-[#F59E0B] border border-[#F59E0B]/30 hover:bg-[#F59E0B]/10 px-4 py-2 rounded-lg transition-colors"
                >
                  <CheckCircle2 size={13} /> Mark remediation complete
                </button>
              )}
              {record.isFailure && remediationComplete && (
                <div className="mt-3 flex items-center gap-2 text-xs text-[#10B981]">
                  <CheckCircle2 size={13} /> Remediation complete — awaiting re-test and closure
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Action bar */}
      <div className="glass-card p-4 flex items-center gap-2 sm:gap-3 flex-wrap" data-testid="action-bar">
        <button data-testid="export-record-pdf" onClick={() => { exportDecisionRecordPDF(record); toast.success('PDF downloaded: apex-' + record.id + '.pdf'); }} className="flex items-center gap-2 bg-[#6366F1] hover:bg-[#818cf8] text-white text-xs sm:text-sm font-medium px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg transition-all shadow-[0_0_8px_rgba(99,102,241,0.15)]">
          <FileText size={14} /> Export as PDF
        </button>
        <button onClick={() => toast.success('Record added to audit package')} className="flex items-center gap-2 bg-[#0F1629] border border-[#1E2A45] hover:border-[#6366F1] text-[#94A3B8] text-sm px-4 py-2.5 rounded-lg transition-colors">
          <Plus size={14} /> Add to audit package
        </button>
        <button data-testid="auditor-test-view-btn" onClick={() => setAuditorView(!auditorView)} className={`flex items-center gap-2 border text-sm px-4 py-2.5 rounded-lg transition-colors ${auditorView ? 'bg-[#6366F1]/10 border-[#6366F1] text-[#6366F1]' : 'bg-[#0F1629] border-[#1E2A45] hover:border-[#6366F1] text-[#94A3B8]'}`}>
          <ClipboardCheck size={14} /> Auditor Test View
        </button>
        <button onClick={() => toast.warning('Record flagged for review')} className="flex items-center gap-2 bg-[#0F1629] border border-[#1E2A45] hover:border-[#F59E0B] text-[#94A3B8] text-sm px-4 py-2.5 rounded-lg transition-colors">
          <Flag size={14} /> Flag for review
        </button>
        <button data-testid="copy-record-id" onClick={() => { navigator.clipboard?.writeText(record.id); toast.success('Record ID copied'); }} className="flex items-center gap-2 text-[#64748B] text-sm px-4 py-2.5 hover:text-[#94A3B8] transition-colors ml-auto">
          <Copy size={14} /> Copy record ID
        </button>
      </div>
    </div>
  );
}
